import React, { useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import ModuleFrame, { Chip, KV, useModuleTab } from '../../components/ModuleFrame';
import { AppFocusChips, useSelectedApp, withApp } from '../../components/AppSelector';
import { updateEntity, useEntityList, friendlyError } from '../../data/entities';
import { apiGet, apiPost } from '../../utils/backendApi';
import { API_BASE } from '../../data/env';
import ListenModule from './ListenModule';
import { LibraryLifecycle } from '../../components/LifecycleRail';

// Data & GraphQL — the schema the registry generates, a sandboxed query
// builder that runs as the caller (same role checks as /graphql), the
// Listen datasets tab, data controls (what the registry already enforces)
// and a readiness view for the selected app.

const sampleQuery = (ent) => {
  const scope = (ent.chain || []).map((a) => `${a}Id: $${a}Id`).join(', ');
  const fields = ent.fields.filter((f) => !['map', 'array'].includes(f.type)).slice(0, 6).map((f) => f.name).join(' ');
  return `query ${ent.name}s(${(ent.chain || []).map((a) => `$${a}Id: ID!`).join(', ')}) {\n  ${ent.name}s(${scope}${scope ? ', ' : ''}limit: 10) {\n    id ${fields}\n  }\n}`;
};

export default function DataModule() {
  const { org, myRole } = useOutletContext();
  const isAdmin = ['admin', 'owner'].includes(myRole);
  const { app } = useSelectedApp();
  const [tab, setTab] = useModuleTab('sources');
  const [schema, setSchema] = useState(null);
  const [schemaErr, setSchemaErr] = useState('');
  const { docs: workspaces } = useEntityList('workspace', { org: org.id });
  const { docs: integrations } = useEntityList('integration', { org: org.id });

  useEffect(() => {
    apiGet(`/data/${org.id}/schema`).then((r) => setSchema(r.entities || [])).catch((e) => setSchemaErr(e.message));
  }, [org.id]);

  const orgEntities = (schema || []).filter((e) => e.chain?.[0] === 'org' || e.name === 'org');
  const linked = app?.links?.dataSourceIds || [];
  const apiIntegs = (integrations || []).filter((i) => i.kind === 'http');

  const [linkErr, setLinkErr] = useState('');
  const toggleSource = async (name) => {
    if (!app) return;
    setLinkErr('');
    const next = linked.includes(name) ? linked.filter((x) => x !== name) : [...linked, name];
    try {
      await updateEntity('app', { org: org.id }, app.id, { links: { ...(app.links || {}), dataSourceIds: next, graphqlReady: next.length > 0 } });
    } catch (e) { setLinkErr(friendlyError(e)); }
  };

  // A check is green only when the thing it names exists and passed.
  const readiness = app ? [
    ['GraphQL reachable', !!API_BASE, 'ENABLE_GRAPHQL on this plane'],
    ['Sources linked to app', linked.length > 0, `${linked.length} entities`],
    ['Datasets available', (workspaces || []).length > 0, 'at least one workspace'],
    ['External APIs tested', apiIntegs.length > 0 && apiIntegs.every((i) => i.lastTest?.ok), apiIntegs.length ? `${apiIntegs.length} HTTP integrations` : 'no HTTP integrations yet'],
  ] : [];

  return (
    <ModuleFrame
      title="Listen"
      subtitle="Data & GraphQL · Sources · Schema · Query builder · Datasets · Controls"
      accent="#4f8cff"
      note={`Listen (data), tab ${tab}${app ? `, app "${app.name}"` : ''}: ${schema ? `${schema.length} GraphQL types` : schemaErr ? 'schema unavailable' : 'schema loading'}. NoVA: create_dataset, list_dataset, create_integration for an API source; the query builder runs GraphQL as you on this screen.`}
      help="Every registry entity is already a GraphQL type with role-checked CRUD. Explore the schema, try queries in the sandbox as yourself, link the sources an app depends on, and bring files in through Listen datasets."
      context={(
        <>
          <AppFocusChips allowNone />
          <Chip tone={schema ? 'ok' : schemaErr ? 'danger' : 'muted'}>{schema ? `${schema.length} GraphQL types` : schemaErr ? 'schema unavailable' : 'loading schema'}</Chip>
        </>
      )}
      lead={app ? <LibraryLifecycle org={org} app={app} moduleId="data" /> : null}
      tabs={[
        { id: 'sources', label: 'Sources' },
        { id: 'schema', label: 'Schema explorer', count: schema?.length },
        { id: 'query', label: 'Query builder' },
        { id: 'datasets', label: 'Datasets (Listen)' },
        { id: 'controls', label: 'Data controls' },
        { id: 'readiness', label: 'Readiness' },
      ]}
      tab={tab}
      onTab={setTab}
      verbs={[
        { label: 'Try a query', hint: 'sandbox, runs as you', onClick: () => setTab('query'), primary: true },
        { label: 'Add a dataset', hint: 'Listen intake', onClick: () => setTab('datasets') },
        { label: 'Connect an API', hint: 'Integrations', to: withApp(`/orgs/${org.id}/integrations`, app) },
      ]}
    >
      {tab === 'sources' && (
        <ModuleFrame.Columns>
          <ModuleFrame.Panel title="Platform entities" meta={app ? `tick the ones ${app.name} reads` : 'select an app to link sources'}>
            {schemaErr && <p className="cs-form-error">{schemaErr}</p>}
            <ul className="mf-list">
              {orgEntities.map((e) => (
                <li key={e.name} className="mf-row">
                  <input type="checkbox" checked={linked.includes(e.name)} disabled={!app} onChange={() => toggleSource(e.name)} aria-label={`link ${e.label}`} />
                  <span className="mf-row-grow"><strong>{e.label}</strong> <span className="mf-muted">{e.name}s · read: {e.access?.read}</span></span>
                  <button type="button" className="mf-muted" style={{ background: 'none', border: 0, cursor: 'pointer' }} onClick={() => setTab('schema')}>fields →</button>
                </li>
              ))}
            </ul>
            {linkErr && <p className="cs-form-error" role="alert">{linkErr}</p>}
          </ModuleFrame.Panel>
          <ModuleFrame.Panel title="Datasets" meta="files on shared storage (Listen)">
            {(workspaces || []).length === 0 && <p className="cs-help">No workspace yet — create one on <Link to={`/orgs/${org.id}`}>Home</Link>, or name a workflow in nQube and a "Main" workspace is created for you.</p>}
            <ul className="mf-list">
              {(workspaces || []).map((w) => (
                <li key={w.id} className="mf-row"><span className="mf-row-grow">{w.name}</span><Link className="mf-muted" to={`/orgs/${org.id}/ws/${w.id}`}>open →</Link></li>
              ))}
            </ul>
          </ModuleFrame.Panel>
          <ModuleFrame.Panel title="External APIs" meta="via Integrations">
            {!apiIntegs.length && <p className="cs-help">None yet. <Link to={withApp(`/orgs/${org.id}/integrations`, app)}>Connect one →</Link></p>}
            <ul className="mf-list">
              {apiIntegs.map((i) => (
                <li key={i.id} className="mf-row">
                  <Chip tone={i.lastTest?.ok ? 'ok' : i.lastTest ? 'danger' : 'muted'}>{i.lastTest ? (i.lastTest.ok ? 'reachable' : 'failing') : 'untested'}</Chip>
                  <span className="mf-row-grow">{i.name} <span className="mf-muted">{i.baseUrl}</span></span>
                </li>
              ))}
            </ul>
          </ModuleFrame.Panel>
        </ModuleFrame.Columns>
      )}

      {tab === 'schema' && (
        <div className="mf-grid-cards" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
          {(schema || []).map((e) => (
            <div key={e.name} className="mf-card">
              <div className="mf-card-title">{e.label} <span className="mf-muted">Ent{e.name[0].toUpperCase()}{e.name.slice(1)}</span></div>
              <div className="mf-muted">{e.collection}{e.parent ? ` under ${e.parent}` : ''} · read {e.access?.read} · write {e.access?.update}</div>
              <table className="mf-table">
                <tbody>
                  {e.fields.map((f) => (
                    <tr key={f.name}>
                      <td>{f.name}</td>
                      <td className="mf-muted">{f.type}{f.enum ? ` (${f.enum.join('|')})` : ''}</td>
                      <td className="mf-muted">{[f.required && 'required', f.serverOnly && 'server-only', f.immutable && 'immutable'].filter(Boolean).join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {tab === 'query' && <QueryBuilder orgId={org.id} schema={schema || []} />}

      {tab === 'datasets' && <ListenModule />}

      {tab === 'controls' && (
        <ModuleFrame.Columns cols={2}>
          <ModuleFrame.Panel title="What is enforced" meta="registry → Firestore rules + GraphQL resolvers">
            <KV rows={[
              ['Tenancy', 'every entity is scoped under orgs/{orgId}; membership is checked on each read/write'],
              ['Roles', 'viewer < member < admin < owner — per entity, per verb, from one registry'],
              ['Server-only fields', 'status, URLs, secrets, audit fields: clients cannot write them'],
              ['Depth limit', 'GraphQL queries are depth-limited (config/app graphql.depthLimit); introspection off in production'],
              ['Knowledge gating', 'retrieval chunks carry orgId + audience; cross-org reads are impossible'],
            ]} />
          </ModuleFrame.Panel>
          <ModuleFrame.Panel title="Environment controls" meta={isAdmin ? 'who may release where' : 'admins edit in Administration'}>
            <KV rows={[
              ['Sandbox', org.envControls?.sandbox || 'member'],
              ['UAT', org.envControls?.uat || 'admin'],
              ['Production', org.envControls?.prod || 'admin'],
            ]} />
            <Link to={`/orgs/${org.id}/administration?tab=environments`} className="mf-muted">Edit in Administration →</Link>
          </ModuleFrame.Panel>
        </ModuleFrame.Columns>
      )}

      {tab === 'readiness' && (
        <ModuleFrame.Panel title={app ? `Readiness · ${app.name}` : 'Readiness'} meta="data prerequisites before Build">
          {!app && <p className="cs-help">Select an app to see its data readiness.</p>}
          <ul className="mf-list">
            {readiness.map(([label, ok, hint]) => (
              <li key={label} className="mf-row"><Chip tone={ok ? 'ok' : 'warn'}>{ok ? '✓' : '!'}</Chip><span className="mf-row-grow">{label}</span><span className="mf-muted">{hint}</span></li>
            ))}
          </ul>
        </ModuleFrame.Panel>
      )}
    </ModuleFrame>
  );
}

function QueryBuilder({ orgId, schema }) {
  const [query, setQuery] = useState('');
  const [vars, setVars] = useState(JSON.stringify({ orgId }, null, 2));
  const [out, setOut] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      let variables = {};
      try { variables = JSON.parse(vars || '{}'); } catch { throw new Error('variables must be valid JSON'); }
      setOut(await apiPost(`/data/${orgId}/query`, { query, variables }));
    } catch (e) { setOut({ errors: [{ message: friendlyError(e) }] }); } finally { setBusy(false); }
  };
  return (
    <ModuleFrame.Columns>
      <ModuleFrame.Panel title="Templates" meta="click to load">
        <ul className="mf-list">
          {schema.filter((e) => e.chain?.length <= 1).map((e) => (
            <li key={e.name}><button type="button" className="mf-row" style={{ width: '100%', cursor: 'pointer', color: 'var(--text)' }} onClick={() => setQuery(sampleQuery(e))}>list {e.label.toLowerCase()}s</button></li>
          ))}
        </ul>
      </ModuleFrame.Panel>
      <ModuleFrame.Panel title="Query" meta="runs as you; mutations refused here" className="mf-span-2">
        <textarea className="mf-textarea" style={{ minHeight: 160, fontFamily: 'ui-monospace, Menlo, monospace' }} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="query { workspaces(orgId: $orgId) { id name } }" aria-label="GraphQL query" />
        <textarea className="mf-textarea" style={{ minHeight: 60, marginTop: 8, fontFamily: 'ui-monospace, Menlo, monospace' }} value={vars} onChange={(e) => setVars(e.target.value)} aria-label="Variables JSON" />
        <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
          <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" disabled={busy || !query.trim()} onClick={run}>{busy ? 'Running…' : 'Run query'}</button>
          {out?.ms !== undefined && <span className="mf-muted">{out.ms} ms</span>}
          <a className="mf-muted" href={`${API_BASE}/graphql`} target="_blank" rel="noreferrer">open GraphiQL ↗</a>
        </div>
        {out && <pre className="mf-code" style={{ marginTop: 10, maxHeight: 360 }}>{JSON.stringify(out.errors?.length ? { errors: out.errors } : out.data, null, 2)}</pre>}
      </ModuleFrame.Panel>
    </ModuleFrame.Columns>
  );
}
